import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from 'axios';
import { getAuthToken } from '../../utils/authUtils';
import { refreshAccessTokenSilently } from '../../utils/apiService';
import { getToggleErrorMessage } from './fetchBotStatus';

function extractToggleApiMessage(error, fallback) {
    const d = error?.response?.data;
    // Prefer user-facing fields; never surface raw technical `details` first.
    const raw =
        (typeof d?.message === 'string' && d.message.trim()) ||
        (typeof d?.error === 'string' && d.error.trim()) ||
        (typeof d?.details === 'string' && d.details.trim()) ||
        error?.message ||
        fallback;
    return getToggleErrorMessage(raw, 'Something went wrong please try again.');
}

export const startBot = createAsyncThunk(
    'startStopBots/startBot',
    async (botId, { rejectWithValue }) => {
        try {
            const backendUrl =  process.env.BACKEND_URL || 'https://apiautobot.idxsolana.io';
            let token = getAuthToken();
            if (!token) {
                try {
                    token = await refreshAccessTokenSilently();
                } catch {
                    token = null;
                }
            }

            if (!token) {
                return rejectWithValue('No authentication token found');
            }

            const response = await axios.post(
                `${backendUrl}/bots/${botId}/start`,
                {}, // Empty data/body for POST
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    timeout: 300000, // 5 minutes timeout
                    validateStatus: (status) => status >= 200 && status < 300,
                }
            );

            return response.data;
        } catch (error) {
            console.error('Start bot error:', error);
            return rejectWithValue(
                extractToggleApiMessage(error, 'Failed to start bot')
            );
        }
    }
);

export const stopBot = createAsyncThunk(
    'startStopBots/stopBot',
    async (botId, { rejectWithValue }) => {
        try {
            const backendUrl =  process.env.BACKEND_URL || 'https://apiautobot.idxsolana.io';
            let token = getAuthToken();
            if (!token) {
                try {
                    token = await refreshAccessTokenSilently();
                } catch {
                    token = null;
                }
            }

            if (!token) {
                return rejectWithValue('No authentication token found');
            }

            const response = await axios.post(
                `${backendUrl}/bots/${botId}/stop`,
                {}, // Empty data/body for POST
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    timeout: 300000, // 5 minutes timeout
                    validateStatus: (status) => status >= 200 && status < 300,
                }
            );

            return response.data;
        } catch (error) {
            console.error('Stop bot error:', error);
            return rejectWithValue(
                extractToggleApiMessage(error, 'Failed to stop bot')
            );
        }
    }
);

const initialState = {
    runningBots: [], // Array of bot IDs that are currently running
    startingBots: [], // Array of bot IDs currently being started
    stoppingBots: [], // Array of bot IDs currently being stopped
    loading: 'idle',
    error: null,
    startLoading: false, // Global start loading flag
    stopLoading: false   // Global stop loading flag
};

function normalizeToggleBotId(botId) {
    return botId != null && botId !== '' ? String(botId) : botId;
}

const startStopSlice = createSlice({
    name: 'startStopBots',
    initialState,
    reducers: {
        resetStartStop(state) {
            state.runningBots = [];
            state.startingBots = [];
            state.stoppingBots = [];
            state.loading = 'idle';
            state.error = null;
            state.startLoading = false;
            state.stopLoading = false;
        },
        addRunningBot(state, action) {
            const botId = normalizeToggleBotId(action.payload);
            if (!state.runningBots.includes(botId)) {
                state.runningBots.push(botId);
            }
        },
        removeRunningBot(state, action) {
            const botId = normalizeToggleBotId(action.payload);
            state.runningBots = state.runningBots.filter(id => id !== botId);
        },
        removeStartingBot(state, action) {
            const botId = normalizeToggleBotId(action.payload);
            state.startingBots = state.startingBots.filter(id => id !== botId);
        },
        removeStoppingBot(state, action) {
            const botId = normalizeToggleBotId(action.payload);
            state.stoppingBots = state.stoppingBots.filter(id => id !== botId);
        },
        updateBotStatus(state, action) {
            const { botId, status } = action.payload;
            const id = normalizeToggleBotId(botId);
            if (status === 'running') {
                if (!state.runningBots.includes(id)) {
                    state.runningBots.push(id);
                }
            } else {
                state.runningBots = state.runningBots.filter(x => x !== id);
            }
        },
        clearError(state) {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Start bot reducers
            .addCase(startBot.pending, (state, action) => {
                const botId = normalizeToggleBotId(action.meta.arg);
                state.startLoading = true;
                state.loading = 'pending';
                state.error = null;

                // Add to starting bots array
                if (!state.startingBots.includes(botId)) {
                    state.startingBots.push(botId);
                }
            })
            .addCase(startBot.fulfilled, (state, action) => {
                const botId = normalizeToggleBotId(action.meta.arg);
                state.startLoading = false;
                state.loading = 'idle';
                state.error = null;

                const payloadStatus = action.payload?.status;
                const deduped = Boolean(action.payload?.deduped);
                const isAsyncPortfolio =
                    Boolean(action.payload?.async) &&
                    (payloadStatus === 'starting' || payloadStatus === 'running');

                // Combo 202: keep starting state until status poll confirms running.
                if (isAsyncPortfolio && payloadStatus === 'starting') {
                    if (!state.startingBots.includes(botId)) {
                        state.startingBots.push(botId);
                    }
                    return;
                }

                // Already running — no need to keep "starting" spinner.
                if (deduped && payloadStatus === 'running') {
                    state.startingBots = state.startingBots.filter(id => id !== botId);
                    if (!state.runningBots.includes(botId)) {
                        state.runningBots.push(botId);
                    }
                    return;
                }

                // Keep in startingBots until UI confirms running (socket / status poll).
                if (!state.startingBots.includes(botId)) {
                    state.startingBots.push(botId);
                }
            })
            .addCase(startBot.rejected, (state, action) => {
                const botId = normalizeToggleBotId(action.meta.arg);
                state.startLoading = false;
                state.loading = 'idle';

                // Remove from starting bots
                state.startingBots = state.startingBots.filter(id => id !== botId);

                state.error = action.payload || action.error.message;
            })

            // Stop bot reducers
            .addCase(stopBot.pending, (state, action) => {
                const botId = normalizeToggleBotId(action.meta.arg);
                state.stopLoading = true;
                state.loading = 'pending';
                state.error = null;

                // Add to stopping bots array
                if (!state.stoppingBots.includes(botId)) {
                    state.stoppingBots.push(botId);
                }
            })
            .addCase(stopBot.fulfilled, (state, action) => {
                const botId = normalizeToggleBotId(action.meta.arg);
                state.stopLoading = false;
                state.loading = 'idle';
                state.error = null;

                const payloadStatus = action.payload?.status;
                const deduped = Boolean(action.payload?.deduped);
                // Combo + V12 (and any future async stop).
                const isAsyncStop =
                    Boolean(action.payload?.async) &&
                    (payloadStatus === 'stopping' || payloadStatus === 'stopped');

                if (isAsyncStop && payloadStatus === 'stopping') {
                    if (!state.stoppingBots.includes(botId)) {
                        state.stoppingBots.push(botId);
                    }
                    return;
                }

                // Already stopped — clear stopping state immediately.
                if (deduped && payloadStatus !== 'running') {
                    state.stoppingBots = state.stoppingBots.filter(id => id !== botId);
                    state.runningBots = state.runningBots.filter(id => id !== botId);
                    return;
                }

                // Keep in stoppingBots until UI confirms stopped (socket / status poll).
                if (!state.stoppingBots.includes(botId)) {
                    state.stoppingBots.push(botId);
                }
            })
            .addCase(stopBot.rejected, (state, action) => {
                const botId = normalizeToggleBotId(action.meta.arg);
                state.stopLoading = false;
                state.loading = 'idle';

                // Remove from stopping bots
                state.stoppingBots = state.stoppingBots.filter(id => id !== botId);

                state.error = action.payload || action.error.message;
            });
    }
});

// Selectors
export const selectIsBotStarting = (state, botId) =>
    state.startStopBots?.startingBots?.includes(normalizeToggleBotId(botId)) || false;

export const selectIsBotStopping = (state, botId) =>
    state.startStopBots?.stoppingBots?.includes(normalizeToggleBotId(botId)) || false;

export const selectIsBotRunning = (state, botId) =>
    state.startStopBots?.runningBots?.includes(normalizeToggleBotId(botId)) || false;

export const selectIsBotToggling = (state, botId) =>
    selectIsBotStarting(state, botId) || selectIsBotStopping(state, botId);

export const {
    resetStartStop,
    addRunningBot,
    removeRunningBot,
    removeStartingBot,
    removeStoppingBot,
    updateBotStatus,
    clearError
} = startStopSlice.actions;

export const startStopReducer = startStopSlice.reducer;
export default startStopReducer;